import { supabase } from './customSupabaseClient.js';

const BUCKET = 'site-media';
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.82;

/**
 * Slugify a name for storage paths
 */
export function slugifyName(name) {
  return String(name || 'image')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'image';
}

/**
 * Build storage object path for fleet or services
 */
export function buildMediaPath(vehicleName, vehicleType) {
  const slug = slugifyName(vehicleName);
  const ts = Date.now();
  const type = String(vehicleType || 'misc')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-');

  if (type === 'service') {
    return `services/${slug}-${ts}.jpg`;
  }
  return `fleet/${type}/${slug}-${ts}.jpg`;
}

/**
 * Resize/compress image in the browser (no Node sharp on Hostinger)
 */
export async function compressImageForUpload(file, maxEdge = MAX_EDGE, quality = JPEG_QUALITY) {
  if (!file || !file.type?.startsWith('image/')) {
    return { blob: file, contentType: file?.type || 'application/octet-stream' };
  }

  // GIF: keep as-is (canvas would flatten animation)
  if (file.type === 'image/gif') {
    return { blob: file, contentType: file.type };
  }

  const bitmap = await createImageBitmap(file);
  try {
    const { width, height } = bitmap;
    const scale = Math.min(1, maxEdge / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, w, h);

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Image compression failed'))),
        'image/jpeg',
        quality
      );
    });

    return { blob, contentType: 'image/jpeg' };
  } finally {
    bitmap.close?.();
  }
}

/**
 * Upload image to Supabase Storage and return public URL
 */
export async function uploadToSiteMedia(file, vehicleName, vehicleType) {
  if (!file) {
    throw new Error('No file selected');
  }

  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData?.session) {
    throw new Error('You must be signed in to upload images');
  }

  const originalSize = file.size;
  const { blob, contentType } = await compressImageForUpload(file);
  const path = buildMediaPath(vehicleName, vehicleType);

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, {
      contentType,
      upsert: true,
      cacheControl: '3600',
    });

  if (uploadError) {
    throw new Error(uploadError.message || 'Storage upload failed');
  }

  const { data: publicData } = supabase.storage.from(BUCKET).getPublicUrl(path);
  const publicUrl = publicData?.publicUrl;

  if (!publicUrl) {
    throw new Error('Upload succeeded but public URL is missing');
  }

  const optimizedSize = blob.size ?? originalSize;
  const savings =
    originalSize > 0
      ? Math.round(((originalSize - optimizedSize) / originalSize) * 1000) / 10
      : 0;

  return {
    success: true,
    optimizedPath: publicUrl,
    originalSize,
    optimizedSize,
    savings: Math.max(0, savings),
    storagePath: path,
  };
}

export default {
  uploadToSiteMedia,
  compressImageForUpload,
  buildMediaPath,
  slugifyName,
};
